import React, { useEffect, useState, useRef } from 'react';
import { TrendingUp, Zap, Eye, DollarSign, Users, Clock, CheckCircle2 } from 'lucide-react';
import { motion, useAnimation, useInView, AnimatePresence } from 'framer-motion';

const LiveBiddingShowcase = () => {
  const [currentBid, setCurrentBid] = useState(250000);
  const [bidCount, setBidCount] = useState(3);
  const [activeDealers, setActiveDealers] = useState(12);
  const [viewCount, setViewCount] = useState(127);
  const [recentBids, setRecentBids] = useState([
    { id: 1, dealer: 'Oslo Bil AS', amount: 250000, time: 'Nå', avatar: '🏢' },
    { id: 2, dealer: 'Bergen Motor', amount: 248000, time: '30 sek siden', avatar: '🚗' },
    { id: 3, dealer: 'Trondheim Auto', amount: 245000, time: '1 min siden', avatar: '🏪' },
  ]);
  const [biddingDealers, setBiddingDealers] = useState([]);
  const [showBidWar, setShowBidWar] = useState(false);
  
  const containerRef = useRef(null);
  const isInView = useInView(containerRef, { once: true, amount: 0.3 });

  const dealerPool = [
    { name: 'Auto Import AS', avatar: '🏢' },
    { name: 'Nord Bil', avatar: '🚙' },
    { name: 'Vest Motor', avatar: '🚗' },
    { name: 'Sør Biler', avatar: '🏪' },
    { name: 'Premium Auto', avatar: '⭐' },
    { name: 'City Cars', avatar: '🏙️' },
    { name: 'AutoNorge', avatar: '🇳🇴' },
    { name: 'Bil Sentrum', avatar: '🎯' },
  ];

  useEffect(() => {
    if (!isInView) return;

    const bidInterval = setInterval(() => {
      const increment = Math.floor(Math.random() * 7000) + 3000;
      const newAmount = currentBid + increment;
      const randomDealer = dealerPool[Math.floor(Math.random() * dealerPool.length)];

      // Show dealer joining the bid war
      setShowBidWar(true);
      setBiddingDealers(prev => [...prev, { ...randomDealer, id: Date.now() }]);
      
      setTimeout(() => {
        setBiddingDealers(prev => prev.slice(1));
      }, 1500);

      setTimeout(() => setShowBidWar(false), 2000);

      setCurrentBid(newAmount);
      setBidCount(prev => prev + 1);
      setActiveDealers(prev => Math.min(50, prev + Math.floor(Math.random() * 2)));
      setViewCount(prev => prev + Math.floor(Math.random() * 5) + 1);

      setRecentBids(prev => [
        { id: Date.now(), dealer: randomDealer.name, amount: newAmount, time: 'Nå', avatar: randomDealer.avatar },
        ...prev.slice(0, 2).map((bid, i) => ({ 
          ...bid, 
          time: i === 0 ? '30 sek siden' : '1 min siden'
        }))
      ]);
    }, 2500);

    return () => clearInterval(bidInterval);
  }, [isInView, currentBid]);

  return (
    <motion.div 
      ref={containerRef}
      className="relative w-full h-full flex items-center"
      initial={{ opacity: 0, y: 20 }}
      animate={isInView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
    >
      <div className="bg-white rounded-3xl shadow-2xl border border-gray-100 p-6 relative overflow-hidden w-full">
        
        {/* Animated background when bidding */}
        <AnimatePresence>
          {showBidWar && (
            <motion.div
              className="absolute inset-0 bg-gradient-to-br from-[#fefe94]/30 via-[#fefe94]/10 to-transparent"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.5 }}
            />
          )}
        </AnimatePresence>

        {/* Header */}
        <div className="relative z-10 mb-4">
          <div className="flex items-center gap-2 mb-2">
            <motion.div 
              className="relative w-2.5 h-2.5 flex-shrink-0"
              animate={{
                scale: [1, 1.2, 1]
              }}
              transition={{
                duration: 1.5,
                repeat: Infinity,
                ease: "easeInOut"
              }}
            >
              <div className="absolute inset-0 bg-[#fefe94] rounded-full"></div>
              <motion.div 
                className="absolute inset-0 bg-[#fefe94] rounded-full"
                animate={{
                  scale: [1, 2.5],
                  opacity: [0.8, 0]
                }}
                transition={{
                  duration: 1.5,
                  repeat: Infinity,
                  ease: "easeOut"
                }}
              />
            </motion.div>
            <h3 className="text-lg sm:text-xl font-bold text-gray-900">Se bilen din få bud</h3>
          </div>
        </div>

        {/* Main Content - Side by Side */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-5 relative z-10">
          
          {/* Left: Your Car Being Bid On */}
          <div className="relative">
            {/* Car Card */}
            <motion.div 
              className="bg-gradient-to-br from-gray-50 to-gray-100 rounded-2xl p-4 relative overflow-hidden"
              animate={{
                scale: showBidWar ? [1, 1.02, 1] : 1,
                boxShadow: showBidWar 
                  ? ['0 10px 30px rgba(0,0,0,0.1)', '0 20px 50px rgba(254,254,148,0.3)', '0 10px 30px rgba(0,0,0,0.1)']
                  : '0 10px 30px rgba(0,0,0,0.1)'
              }}
              transition={{ duration: 0.6 }}
            >
              {/* "YOUR CAR" Badge */}
              <div className="absolute top-2 left-2 bg-[#fefe94] text-gray-900 text-xs font-bold px-2.5 py-0.5 rounded-full z-10">
                DIN BIL
              </div>

              {/* Viewing indicator */}
              <motion.div 
                className="absolute top-2 right-2 bg-white/90 backdrop-blur-sm text-gray-900 text-xs font-semibold px-2.5 py-0.5 rounded-full flex items-center gap-1 z-10"
                key={viewCount}
                initial={{ scale: 1.2 }}
                animate={{ scale: 1 }}
              >
                <Eye className="w-3 h-3" />
                {viewCount} ser nå
              </motion.div>

              {/* Car Illustration */}
              <div className="mt-6">
                <svg viewBox="0 0 300 180" className="w-full h-auto">
                  <defs>
                    <linearGradient id="carGradient" x1="0%" y1="0%" x2="0%" y2="100%">
                      <stop offset="0%" stopColor="#374151" />
                      <stop offset="100%" stopColor="#1f2937" />
                    </linearGradient>
                    <linearGradient id="windowGradient" x1="0%" y1="0%" x2="0%" y2="100%">
                      <stop offset="0%" stopColor="#9ca3af" stopOpacity="0.3" />
                      <stop offset="100%" stopColor="#6b7280" stopOpacity="0.2" />
                    </linearGradient>
                  </defs>

                  <g>
                    <rect x="50" y="90" width="200" height="40" rx="8" fill="url(#carGradient)" />
                    <path d="M 90 90 L 110 55 L 190 55 L 210 90 Z" fill="url(#carGradient)" />
                    <path d="M 95 87 L 113 60 L 145 60 L 145 87 Z" fill="url(#windowGradient)" />
                    <path d="M 155 87 L 155 60 L 187 60 L 205 87 Z" fill="url(#windowGradient)" />
                    <rect x="110" y="88" width="80" height="3" rx="1.5" fill="#fefe94" />
                    
                    <circle cx="200" cy="130" r="20" fill="#0f172a" />
                    <circle cx="200" cy="130" r="15" fill="#1e293b" />
                    <circle cx="200" cy="130" r="8" fill="#374155" />
                    <circle cx="200" cy="130" r="4" fill="#fefe94" />
                    
                    <circle cx="100" cy="130" r="20" fill="#0f172a" />
                    <circle cx="100" cy="130" r="15" fill="#1e293b" />
                    <circle cx="100" cy="130" r="8" fill="#374155" />
                    <circle cx="100" cy="130" r="4" fill="#fefe94" />
                    
                    <circle cx="235" cy="105" r="6" fill="#fefe94" opacity="0.8" />
                  </g>
                </svg>
              </div>

              {/* Dealers watching/bidding */}
              <div className="mt-2 flex items-center justify-center gap-1 min-h-[32px]">
                <AnimatePresence mode="popLayout">
                  {biddingDealers.map((dealer) => (
                    <motion.div
                      key={dealer.id}
                      className="text-2xl"
                      initial={{ scale: 0, y: 20 }}
                      animate={{ scale: 1, y: 0 }}
                      exit={{ scale: 0, y: -20 }}
                      transition={{ type: "spring", stiffness: 500, damping: 25 }}
                    >
                      {dealer.avatar}
                    </motion.div>
                  ))}
                </AnimatePresence>
              </div>
            </motion.div>

            {/* Live Stats Below Car */}
            <div className="grid grid-cols-3 gap-2 mt-3">
              <motion.div 
                className="bg-gray-50 rounded-xl p-2 text-center"
                whileHover={{ scale: 1.05 }}
              >
                <div className="flex items-center justify-center gap-1 mb-0.5">
                  <Users className="w-3.5 h-3.5 text-gray-600" />
                  <motion.span 
                    className="text-xl font-black text-gray-900"
                    key={activeDealers}
                    initial={{ scale: 1.3 }}
                    animate={{ scale: 1 }}
                  >
                    {activeDealers}
                  </motion.span>
                </div>
                <p className="text-[10px] text-gray-600">Aktive</p>
              </motion.div>

              <motion.div 
                className="bg-[#fefe94]/20 border-2 border-[#fefe94] rounded-xl p-2 text-center"
                whileHover={{ scale: 1.05 }}
              >
                <div className="flex items-center justify-center gap-1 mb-0.5">
                  <Zap className="w-3.5 h-3.5 text-gray-900" />
                  <motion.span 
                    className="text-xl font-black text-gray-900"
                    key={bidCount}
                    initial={{ scale: 1.3 }}
                    animate={{ scale: 1 }}
                  >
                    {bidCount}
                  </motion.span>
                </div>
                <p className="text-[10px] text-gray-600">Bud</p>
              </motion.div>

              <motion.div 
                className="bg-gray-50 rounded-xl p-2 text-center"
                whileHover={{ scale: 1.05 }}
              >
                <div className="flex items-center justify-center gap-1 mb-0.5">
                  <Clock className="w-3.5 h-3.5 text-gray-600" />
                  <span className="text-xl font-black text-gray-900">2m</span>
                </div>
                <p className="text-[10px] text-gray-600">Igjen</p>
              </motion.div>
            </div>
          </div>

          {/* Right: Live Bidding Action */}
          <div>
            {/* Current Highest Bid */}
            <motion.div 
              className="bg-gradient-to-br from-[#fefe94]/30 to-[#fefe94]/10 border-2 border-[#fefe94] rounded-2xl p-4 mb-3 relative overflow-hidden"
              animate={{
                scale: showBidWar ? [1, 1.03, 1] : 1,
              }}
              transition={{ duration: 0.4 }}
            >
              {/* Flash effect */}
              <AnimatePresence>
                {showBidWar && (
                  <motion.div
                    className="absolute inset-0 bg-[#fefe94]"
                    initial={{ opacity: 0.5 }}
                    animate={{ opacity: 0 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.6 }}
                  />
                )}
              </AnimatePresence>

              <div className="relative z-10">
                <div className="flex items-center justify-between mb-1">
                  <span className="text-[10px] font-bold text-gray-600 uppercase tracking-wide">Høyeste bud</span>
                  <motion.div
                    animate={{
                      rotate: showBidWar ? [0, -10, 10, 0] : 0
                    }}
                  >
                    <TrendingUp className="w-4 h-4 text-gray-700" />
                  </motion.div>
                </div>
                
                <motion.div 
                  className="text-3xl font-black text-gray-900 mb-1"
                  key={currentBid}
                  initial={{ y: 20, opacity: 0 }}
                  animate={{ y: 0, opacity: 1 }}
                  transition={{ type: "spring", stiffness: 500, damping: 30 }}
                >
                  {currentBid.toLocaleString('nb-NO')} kr
                </motion.div>

                <div className="flex items-center gap-1.5 text-xs">
                  <CheckCircle2 className="w-3.5 h-3.5 text-gray-400" />
                  <span className="text-gray-600">
                    +{((currentBid - 250000) / 1000).toFixed(0)}k mer enn start
                  </span>
                </div>
              </div>
            </motion.div>

            {/* Live Bid Stream */}
            <div className="space-y-2">
              <div className="text-[10px] font-semibold text-gray-500 uppercase tracking-wide mb-2">
                Live budstrøm
              </div>
              
              <AnimatePresence mode="popLayout">
                {recentBids.map((bid, index) => (
                  <motion.div
                    key={bid.id}
                    layout
                    initial={{ opacity: 0, x: 30, scale: 0.9 }}
                    animate={{ opacity: 1, x: 0, scale: 1 }}
                    exit={{ opacity: 0, x: -30, scale: 0.9 }}
                    transition={{ 
                      type: "spring", 
                      stiffness: 500, 
                      damping: 35,
                      delay: index * 0.03
                    }}
                    className="bg-white border-2 rounded-xl p-3 flex items-center gap-2.5 shadow-sm"
                    style={{
                      borderColor: index === 0 ? '#fefe94' : '#e5e7eb'
                    }}
                  >
                    <motion.div 
                      className="text-2xl"
                      animate={index === 0 ? {
                        scale: [1, 1.2, 1],
                        rotate: [0, -10, 10, 0]
                      } : {}}
                      transition={{ duration: 0.5 }}
                    >
                      {bid.avatar}
                    </motion.div>
                    
                    <div className="flex-1 min-w-0">
                      <p className="font-bold text-sm text-gray-900 truncate">{bid.dealer}</p>
                      <p className="text-[10px] text-gray-500">{bid.time}</p>
                    </div>

                    <div className="text-right">
                      <motion.p 
                        className="text-base font-bold text-gray-900"
                        initial={index === 0 ? { scale: 1.3, color: '#fefe94' } : {}}
                        animate={index === 0 ? { scale: 1, color: '#111827' } : {}}
                      >
                        {bid.amount.toLocaleString('nb-NO')} kr
                      </motion.p>
                      {index === 0 && (
                        <motion.span
                          className="text-[10px] font-bold text-[#fefe94] bg-[#fefe94]/20 px-1.5 py-0.5 rounded-full"
                          initial={{ scale: 0 }}
                          animate={{ scale: 1 }}
                        >
                          NYE!
                        </motion.span>
                      )}
                    </div>
                  </motion.div>
                ))}
              </AnimatePresence>
            </div>
          </div>
        </div>

        {/* Bottom Info */}
        <motion.div 
          className="mt-4 pt-4 border-t border-gray-200 text-center"
          initial={{ opacity: 0 }}
          animate={isInView ? { opacity: 1 } : {}}
          transition={{ delay: 0.8 }}
        >
          <p className="text-xs text-gray-600">
            Slik ser det ut under budrunden • Over 2 000 forhandlere kan gi bud
          </p>
        </motion.div>
      </div>
    </motion.div>
  );
};

export default LiveBiddingShowcase;
